import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CurrencyDisplay } from '@/components/ui/stat-card';
import { FileText, Car, User, Phone, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import SectionDrawer, { DesktopNav } from '@/components/navigation/SectionDrawer';
import { useDriverLayout } from '@/components/layout/DriverLayout';
import { useDriverContract } from '@/hooks/useDriverContract';
import ContractInsightOverview from '@/components/contracts/insights/ContractInsightOverview';
import ContractInsightPayments from '@/components/contracts/insights/ContractInsightPayments';
import ContractInsightInterest from '@/components/contracts/insights/ContractInsightInterest';
import ContractInsightVehicle from '@/components/contracts/insights/ContractInsightVehicle';
import ContractInsightLoans from '@/components/contracts/insights/ContractInsightLoans';
import ContractInsightInsurance from '@/components/contracts/insights/ContractInsightInsurance';
import ContractInsightDocuments from '@/components/contracts/insights/ContractInsightDocuments';
import ContractInsightMaintenance from '@/components/contracts/insights/ContractInsightMaintenance';
import ContractInsightInspections from '@/components/contracts/insights/ContractInsightInspections';
import ContractInsightRoadworthiness from '@/components/contracts/insights/ContractInsightRoadworthiness';

import { useAgreementDocuments } from '@/hooks/useAgreements';
import { awaitingMySignature } from '@/lib/esign';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { inspectionBadgeCount, type ContractInspections } from '@/lib/inspections';

// Type for the RPC response — mirrored from fleet-admin
export interface ContractInsightsData {
    contract: {
        id: number;
        signed_at: string;
        sold_at: number;
        deposit_id: number | null;
        driver_id: number;
        vehicle_id: number;
        created_at: string;
        overdue_interest_rate: number | null;
        management_rate: number | null;
    } | null;
    current_status: string | null;
    driver: {
        id: string;
        driver_id: number;
        first_name: string;
        middle_name: string | null;
        last_name: string;
        phone: string | null;
        photo: string | null;
        address: string | null;
    } | null;
    vehicle: {
        id: number;
        make: string;
        model: string;
        vin: string | null;
        color: string | null;
        manufactured_at: number | null;
        registration_number: string | null;
        tracker?: {
            id: number;
            serial_number: string | null;
            sim_number: string | null;
        } | null;
    } | null;
    repayments: Array<{
        id: number;
        description: string | null;
        transaction_id: number;
        amount: number;
        paid_at: string;
    }>;
    repayments_total: number;
    payments_count: number;
    weekly_rate: number;
    // Overdue interest as `public.calculate_overdue_interest` computed it, composed into this
    // RPC (migration 20260823010000). `monday`, `paid_at` and `first_full_payment_week` are
    // DATE strings — parse them on the local calendar (parseISO), never as instants.
    overdue: {
        rate_missing: boolean;
        /** Signed: negative is interest owed. */
        total_overdue_interest: number;
        first_full_payment_week: string | null;
        weeks: Array<{
            monday: string;
            expected: number;
            total_paid: number;
            total_interest: number;
            sales: Array<{
                id: number;
                amount: number;
                paid_at: string;
                description: string | null;
                remarks: string | null;
                overdue_interest: number | null;
                apply_overdue_interest: boolean;
                surplus_overdue_interest: number | null;
            }>;
        }>;
    } | null;
    // The protected numbers, computed server-side so every client shows the same figure.
    // `overdue_total` is |overdue.total_overdue_interest|; `outstanding` is clamped at 0.
    overdue_total: number;
    total_value: number;
    total_paid: number;
    outstanding: number;
    allocation_summary: {
        contract: number;
        loans: number;
        insurance_loans: number;
    } | null;
    loans: Array<{
        id: number;
        interest_rate: number | null;
        /**
         * THE TRANSACTION'S narration — the bank line that moved the money, which is not what
         * the advance is FOR. See `purpose` below.
         */
        description: string | null;
        /**
         * `loans.description` — WHAT THE ADVANCE IS FOR, and the value the Schedule prints as
         * `{{loan.purpose}}`. Optional because the key is additive: it arrives with the
         * `get_contract_insights` migration and is simply absent on a payload served before it,
         * which every caller here reads as "fall back to the narration".
         */
        purpose?: string | null;
        amount: number;
        lent_at: string;
        agreement: { signed_at: string | null; agreement_url: string | null } | null;
        instalments: Array<{ amount: number; starts_at: string }> | null;
        repayments_total: number;
        allocated_total: number;
        recent_allocations: Array<{
            repayment_id: number;
            amount: number;
            payment_amount: number;
            payment_date: string;
        }>;
        allocation_count: number;
        repayments: Array<{ id: number; amount: number; paid_at: string }>;
    }>;
    insurances: Array<{
        id: number;
        interest_rate: number | null;
        valued_at: number | null;
        starts_at: string | null;
        expires_at: string | null;
        certificate_url: string | null;
        loan: {
            id: number;
            amount: number;
            interest_rate: number | null;
            repayments_total: number;
            deposit_total: number;
            allocated_total: number;
            recent_allocations: Array<{
                repayment_id: number;
                amount: number;
                payment_amount: number;
                payment_date: string;
            }>;
            allocation_count: number;
            agreement_url: string | null;
            instalments: Array<{ amount: number; starts_at: string }> | null;
            repayments: Array<{ id: number; transaction_id: number; amount: number; paid_at: string; description: string | null; is_deposit?: boolean }>;
        } | null;
        premium: number | null;
    }>;
    maintenances: Array<{
        id: number;
        type: string | null;
        description: string | null;
        amount: number;
        started_at: string;
    }>;
    // The old arm: every kind of visit on the contract, re-checks included, as instants. No live
    // view reads it — the Summary tile, the Inspections section and its badge all read
    // `get_contract_inspections` (src/lib/inspections.ts). Only ContractInsightCompliance.tsx,
    // which nothing imports, still does. Kept while the RPC returns it (PLAN.md §5, §14).
    inspections: Array<{
        id: number;
        inspection_at: string;
        description: string | null;
        photo_count: number;
    }>;
    // `amount` is NET — deposit credits minus refunds. `paid_total` is the gross
    // credited figure and `refunded_total` what has gone back out, so a card can
    // show the net without contradicting a gross payment list.
    deposit: {
        amount: number | null;
        paid_total: number | null;
        refunded_total: number | null;
        count: number;
        refund_count: number;
        status: 'PAID' | 'PARTIALLY_REFUNDED' | 'REFUNDED' | null;
        payments: Array<{ id: number; amount: number; paid_at: string }> | null;
        refunds: Array<{
            id: number;
            deposit_id: number;
            transaction_id: number;
            amount: number;
            refunded_at: string;
            description: string | null;
            remarks: string | null;
        }>;
    } | null;
    repayment_notes: Array<{
        id: number;
        period_start: string;
        period_end: string;
        reason: string | null;
        created_at: string;
    }>;
    status_history: Array<{
        status: string;
        created_at: string;
    }>;
    witnesses: Array<{
        id: number;
        name: string;
        address: string | null;
        phone: string | null;
    }>;
    agreements: Array<{
        type: string;
        signed_at: string | null;
        agreement_url: string | null;
    }>;
    instalments: Array<{
        id: number;
        amount: number;
        starts_at: string;
    }>;
    driver_identification: Array<{
        id: number;
        type: 'DRIVING_LICENCE' | 'NATIONAL_ID';
        document_url: string | null;
        expires_at: string | null;
    }>;
    vehicle_roadworthiness: Array<{
        id: number;
        starts_at: string;
        certificate_url: string | null;
        transaction_id: number | null;
    }>;
    loan_agreements: Array<{
        id: number;
        loan_id: number;
        signed_at: string | null;
        agreement_url: string | null;
        /** The disbursement TRANSACTION's narration, kept as the fallback for `loan_purpose`. */
        loan_description: string | null;
        /** `loans.description` — the Schedule's `{{loan.purpose}}`; additive, hence optional. */
        loan_purpose?: string | null;
        loan_amount: number;
    }>;
    insurance_loan_agreements: Array<{
        id: number;
        loan_id: number;
        signed_at: string | null;
        agreement_url: string | null;
        insurance_id: number;
    }>;
    vehicle_trackers: Array<{
        id: number;
        type: 'primary' | 'secondary';
        serial_number: string | null;
        sim_number: string | null;
        created_at: string;
    }>;
    vehicle_registrations: Array<{
        id: number;
        number: string;
        type: string;
        certificate_url: string | null;
        transaction_id: number | null;
    }>;
    deposit_agreements: Array<{
        id: number;
        contract_deposit_id: number;
        signed_at: string | null;
        agreement_url: string | null;
        deposit_amount: number;
        deposit_date: string;
    }>;
}

const getStatusBadge = (status: string | null) => {
    switch (status) {
        case 'IN_PROGRESS':
            return { label: 'Active', className: 'border-green-200 bg-green-50 text-green-700' };
        case 'COMPLETED':
            return { label: 'Completed', className: 'border-blue-200 bg-blue-50 text-blue-700' };
        case 'TERMINATED':
            return { label: 'Terminated', className: 'border-red-200 bg-red-50 text-red-700' };
        default:
            return { label: 'Draft', className: 'border-gray-200 bg-gray-50 text-gray-600' };
    }
};

export default function ContractDetail() {
    const { contractId: contractIdParam, section: sectionParam } = useParams<{ contractId: string; section: string }>();
    const navigate = useNavigate();
    const { activeContract, allContracts, isLoading: contractsLoading } = useDriverContract();
    const driverLayout = useDriverLayout();
    const [activeSection, setActiveSection] = useState(sectionParam || 'summary');
    const [navSlot, setNavSlot] = useState<HTMLElement | null>(null);

    // Find the desktop nav slot in the header
    useEffect(() => {
        const slot = document.getElementById('desktop-nav-slot');
        if (slot) setNavSlot(slot);
    }, []);

    // Determine which contract to load
    const requestedId = contractIdParam ? parseInt(contractIdParam, 10) : null;

    // Validate that the requested contract belongs to this driver
    const isOwned = requestedId
        ? allContracts.some((c) => c.id === requestedId)
        : true; // no param means we'll use activeContract

    const contractId = isOwned && requestedId
        ? requestedId
        : activeContract?.id;

    // If we landed on /contract/:contractId, redirect to /:section to hide the ID
    useEffect(() => {
        if (contractIdParam) {
            navigate(`/${activeSection}`, { replace: true });
        }
    }, [contractIdParam, activeSection, navigate]);

    // If the driver is trying to access a contract that isn't theirs, redirect
    useEffect(() => {
        if (requestedId && !contractsLoading && allContracts.length > 0 && !isOwned) {
            navigate(`/${activeSection}`, { replace: true });
        }
    }, [requestedId, contractsLoading, allContracts, isOwned, activeSection, navigate]);

    const { data, isLoading: dataLoading, error } = useQuery({
        queryKey: ['contract-insights', contractId],
        queryFn: async () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error } = await (supabase as any).rpc('get_contract_insights', {
                contract_id_param: contractId,
            });
            if (error) throw error;
            return data as ContractInsightsData;
        },
        enabled: !!contractId && isOwned,
    });

    // The monthly inspection: this round's status, the open items and the contract's own visits,
    // all worked out by the server from the lines graded on this contract. The function returns
    // jsonb, so its payload shape is the one written in src/lib/inspections.ts.
    const inspectionsQuery = useQuery({
        queryKey: ['contract-inspections', contractId],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_contract_inspections', {
                p_contract_id: contractId,
            });
            if (error) throw error;
            return (data ?? null) as ContractInspections | null;
        },
        enabled: !!contractId && isOwned,
    });

    const driverPhotoUrl = useSignedUrl(data?.driver?.photo);

    // The badge sits on THIS contract's Documents tab, so it counts what is waiting inside
    // that tab and nothing else: `get_my_pending_signatures` is session-scoped, and a driver
    // holding several contracts would see another contract's work counted here. Same query
    // (and same cache entry) the Documents section itself reads, under the same predicate.
    const { data: contractDocuments } = useAgreementDocuments(contractId);
    const sectionBadges = {
        documents: (contractDocuments ?? []).filter(awaitingMySignature).length,
        // Each open Must fix on this contract, plus one while the car still has to come in.
        inspections: inspectionBadgeCount(inspectionsQuery.data?.current),
    };

    const isLoading = contractsLoading || dataLoading;

    // Scroll to top when section changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeSection]);

    // Update URL to reflect active section
    const handleSectionChange = (section: string) => {
        setActiveSection(section);
        navigate(`/${section}`, { replace: true });
    };

    if (isLoading) {
        return (
            <div className="space-y-4 animate-fade-in px-4 pt-4">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (error || !data?.contract) {
        return (
            <div className="space-y-6 animate-fade-in px-4 pt-6">
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                    <FileText className="h-12 w-12 mb-4" />
                    <p className="text-lg">Contract not found</p>
                    <p className="text-sm text-center">
                        {contractsLoading
                            ? 'Loading your contracts...'
                            : 'No contract is associated with your account.'
                        }
                    </p>
                </div>
            </div>
        );
    }

    const statusBadge = getStatusBadge(data.current_status);
    const driverName = data.driver
        ? [data.driver.first_name, data.driver.middle_name, data.driver.last_name].filter(Boolean).join(' ') || 'Driver'
        : 'Driver';
    const vehicleDisplay = data.vehicle?.registration_number || `${data.vehicle?.make || ''} ${data.vehicle?.model || ''}`.trim() || 'No Vehicle';

    // Total contract value, as the database computed it (vehicle + advances + insurance
    // financing + overdue interest). The formula lives in get_contract_insights now.
    const totalContractValue = data.total_value;

    // Section titles and descriptions for non-summary pages
    const sectionMeta: Record<string, { title: string; description: string }> = {
        payments: { title: 'Payments', description: 'Weekly payment history and records.' },
        interest: { title: 'Interest', description: 'Overdue interest breakdown and charges.' },
        vehicle: { title: 'Vehicle', description: 'Repayment schedule and payment history.' },
        loans: { title: 'Advances', description: 'Active advances and repayment progress.' },
        insurance: { title: 'Insurance', description: 'Insurance policies and repayment status.' },
        maintenance: { title: 'Maintenance', description: 'Service and repair records.' },
        roadworthiness: { title: 'Roadworthiness', description: 'Roadworthy certificate status.' },
        inspections: { title: 'Inspections', description: 'Your monthly inspection and its record.' },
        documents: { title: 'Documents', description: 'Contracts, agreements and certificates.' },
    };

    // The Summary, which also stands in for an unknown section.
    const overview = (
        <ContractInsightOverview
            data={data}
            inspections={inspectionsQuery.data}
            inspectionsFailed={inspectionsQuery.isError}
            onRetryInspections={() => { void inspectionsQuery.refetch(); }}
        />
    );

    // Render active section content
    const renderSection = () => {
        switch (activeSection) {
            case 'summary':
                return overview;
            case 'payments':
                return <ContractInsightPayments data={data} />;
            case 'interest':
                return <ContractInsightInterest data={data} />;
            case 'vehicle':
                return <ContractInsightVehicle data={data} />;
            case 'loans':
                return <ContractInsightLoans data={data} />;
            case 'insurance':
                return <ContractInsightInsurance data={data} />;
            case 'maintenance':
                return <ContractInsightMaintenance data={data} />;
            case 'roadworthiness':
                return <ContractInsightRoadworthiness data={data} />;
            case 'inspections':
                return (
                    <ContractInsightInspections
                        inspections={inspectionsQuery.data}
                        isLoading={inspectionsQuery.isLoading}
                        isError={inspectionsQuery.isError}
                        onRetry={() => { void inspectionsQuery.refetch(); }}
                    />
                );

            case 'documents':
                return (
                    <ContractInsightDocuments
                        data={data}
                    />
                );
            default:
                return overview;
        }
    };

    const currentMeta = sectionMeta[activeSection];

    return (
        <div className="animate-fade-in">
            {/* Portal: Desktop NavigationMenu into main header */}
            {navSlot && createPortal(
                <DesktopNav activeSection={activeSection} onSectionChange={handleSectionChange} badges={sectionBadges} />,
                navSlot
            )}
            {/* ══════════════════════════════════════════ */}
            {/* MOBILE LAYOUT (< lg)                      */}
            {/* ══════════════════════════════════════════ */}
            <div className="lg:hidden">
                {/* Sticky Section Navigation Bar */}
                <div className="sticky top-14 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                    <div className="flex items-center justify-between px-4 py-2">
                        <SectionDrawer
                            activeSection={activeSection}
                            onSectionChange={handleSectionChange}
                            onSignOut={driverLayout?.handleSignOut}
                            badges={sectionBadges}
                        />
                        {allContracts.length > 1 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="text-xs gap-1">
                                        #{data.contract.id}
                                        <ChevronDown className="h-3 w-3" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {allContracts.map((c) => (
                                        <DropdownMenuItem
                                            key={c.id}
                                            onClick={() => navigate(`/contract/${c.id}`)}
                                            className="flex items-center justify-between gap-4"
                                        >
                                            <span>Contract #{c.id}</span>
                                            <Badge
                                                variant="outline"
                                                className={`text-[10px] ${getStatusBadge(c.current_status).className}`}
                                            >
                                                {getStatusBadge(c.current_status).label}
                                            </Badge>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </div>

                {/* Compact Mobile Header — only on summary */}
                {activeSection === 'summary' && (
                    <div className="px-4 pt-4 pb-2">
                        <div className="border rounded-xl p-4 bg-gradient-to-br from-background via-background to-muted/30 space-y-3">
                            <div className="flex items-center gap-3">
                                {driverPhotoUrl ? (
                                    <img src={driverPhotoUrl} alt={driverName} className="h-14 w-14 rounded-full object-cover border-2 border-background shadow-md ring-2 ring-primary/20 shrink-0" />
                                ) : (
                                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/5 shadow-md ring-2 ring-primary/20 shrink-0">
                                        <User className="h-7 w-7 text-primary" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h1 className="text-lg font-bold tracking-tight truncate">{driverName}</h1>
                                        <Badge variant="outline" className={`text-[10px] ${statusBadge.className} ${data.current_status === 'IN_PROGRESS' ? 'animate-pulse' : ''}`}>
                                            {statusBadge.label}
                                        </Badge>
                                    </div>
                                    {data.driver?.phone && (
                                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                            <Phone className="h-3 w-3" />
                                            {data.driver.phone}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-baseline justify-between">
                                <div className="flex items-baseline gap-1.5">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Total</p>
                                    <p className="text-lg font-bold"><CurrencyDisplay amount={totalContractValue} /></p>
                                </div>
                                {data.weekly_rate > 0 && (
                                    <p className="text-xs text-muted-foreground">GHS {data.weekly_rate.toLocaleString()}/wk</p>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted/50 text-xs"><FileText className="h-3 w-3" />#{data.contract.id}</span>
                                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted/50 text-xs"><Car className="h-3 w-3" />{vehicleDisplay}</span>
                                {data.contract.signed_at && new Date(data.contract.signed_at).getFullYear() > 1970 && (
                                    <span className="px-2.5 py-1 rounded-full bg-muted/50 text-xs">{format(new Date(data.contract.signed_at), 'MMM yyyy')}</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Mobile Section Content */}
                <div className="px-4 pb-8 pt-2">
                    {currentMeta && (
                        <div className="mb-4">
                            <h1 className="text-2xl font-bold tracking-tight">{currentMeta.title}</h1>
                            <p className="text-sm text-muted-foreground mt-1">{currentMeta.description}</p>
                        </div>
                    )}
                    {renderSection()}
                </div>
            </div>

            {/* ══════════════════════════════════════════ */}
            {/* DESKTOP LAYOUT (≥ lg)                     */}
            {/* ══════════════════════════════════════════ */}
            <div className="hidden lg:block space-y-6 px-4 sm:px-6 lg:px-8 pt-6">
                {/* Premium Header Card — only on summary */}
                {activeSection === 'summary' && (
                    <div className="border rounded-xl p-6 bg-gradient-to-br from-background via-background to-muted/30 backdrop-blur-sm">
                        <div className="flex items-start justify-between gap-6">
                            {/* Left: Driver Info */}
                            <div className="flex items-start gap-4">
                                {driverPhotoUrl ? (
                                    <img src={driverPhotoUrl} alt={driverName} className="h-[88px] w-[88px] rounded-full object-cover border-4 border-background shadow-lg ring-2 ring-primary/20" />
                                ) : (
                                    <div className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/5 shadow-lg ring-2 ring-primary/20">
                                        <User className="h-11 w-11 text-primary" />
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-3">
                                        <h1 className="text-2xl font-bold tracking-tight">{driverName}</h1>
                                        <Badge variant="outline" className={`${statusBadge.className} ${data.current_status === 'IN_PROGRESS' ? 'animate-pulse' : ''}`}>
                                            {statusBadge.label}
                                        </Badge>
                                    </div>
                                    {data.driver?.phone && (
                                        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                                            <Phone className="h-3.5 w-3.5" />
                                            {data.driver.phone}
                                        </p>
                                    )}
                                    <div className="flex flex-wrap gap-3 text-xs">
                                        <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted/50">
                                            <FileText className="h-3 w-3" />
                                            Contract #{data.contract.id}
                                        </span>
                                        <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted/50">
                                            <Car className="h-3 w-3" />
                                            {vehicleDisplay}
                                        </span>
                                        {data.contract.signed_at && new Date(data.contract.signed_at).getFullYear() > 1970 && (
                                            <span className="px-2 py-1 rounded-full bg-muted/50">
                                                Signed {format(new Date(data.contract.signed_at), 'MMM d, yyyy')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right: Total Value */}
                            <div className="text-right flex flex-col justify-between self-stretch">
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Value</p>
                                    <p className="text-3xl font-bold mt-1"><CurrencyDisplay amount={totalContractValue} /></p>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {data.weekly_rate > 0 && `GHS ${data.weekly_rate.toLocaleString()}/week`}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Desktop Section Content */}
                <div className="space-y-6">
                    {currentMeta && (
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">{currentMeta.title}</h1>
                            <p className="text-muted-foreground mt-1">{currentMeta.description}</p>
                        </div>
                    )}
                    {renderSection()}
                </div>
            </div>
        </div>
    );
}
